import { Image, MessageCircleQuestion } from "lucide-react";
// biome-ignore lint/style/useImportType: Classic JSX runtime requires React.
import React from "react";
import { useTranslation } from "react-i18next";
import type { ScreenshotRef } from "../screenshots";

export const occurrenceActionClass =
  "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-md border border-outline bg-surface px-2.5 text-xs font-medium text-text-mid transition hover:bg-surface-strong hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

/** The same recorded evidence in a case timeline, step inspector, or comparison. */
export default function ProcessOccurrenceEvidence({
  description,
  metadata,
  objects,
  screenshots,
  onShowScreenshots,
  onAsk,
  children,
}: {
  description: string;
  metadata?: React.ReactNode;
  objects: { object: string; qualifier: string }[];
  screenshots: ScreenshotRef[];
  onShowScreenshots: (screenshots: ScreenshotRef[]) => void;
  onAsk?: () => void;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const seen = new Set<string>();
  const uniqueObjects = objects.filter(({ object, qualifier }) => {
    const key = `${object.trim()}\u0000${qualifier.trim()}`;
    if (!object.trim() || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        {metadata && <div className="text-xs text-text-dim">{metadata}</div>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {screenshots.length > 0 && (
            <button
              type="button"
              onClick={() => onShowScreenshots(screenshots)}
              className="inline-flex min-h-8 items-center justify-center gap-1.5 rounded-md border border-info/20 bg-info/10 px-2.5 text-xs font-medium text-info-strong transition hover:bg-info/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/40"
            >
              <Image className="h-3.5 w-3.5" aria-hidden="true" />
              {t("processPanel.caseScreenshots", { count: screenshots.length })}
            </button>
          )}
          {children}
          {onAsk && (
            <button
              type="button"
              onClick={onAsk}
              className="inline-flex min-h-8 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-text-mid transition hover:bg-surface-strong hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
              {t("processPanel.caseAskStep")}
            </button>
          )}
        </div>
      </div>
      <p className="whitespace-pre-line break-words text-sm leading-relaxed text-text-mid">
        {description.trim() || t("no_description")}
      </p>
      {uniqueObjects.length > 0 && (
        <details className="rounded-md border border-outline bg-surface px-3 py-2">
          <summary className="cursor-pointer text-xs font-medium text-text-mid focus-visible:ring-2 focus-visible:ring-primary/40">
            {t("processPanel.caseObjects", { count: uniqueObjects.length })}
          </summary>
          <ul className="mt-2 space-y-2">
            {uniqueObjects.map(({ object, qualifier }) => (
              <li
                key={`${object}\u0000${qualifier}`}
                className="break-words text-xs leading-relaxed text-text-mid"
              >
                {qualifier && (
                  <span className="mr-2 text-text-dim">{qualifier.replace(/-/g, " ")}</span>
                )}
                {object}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
